'use client'

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, HelpCircle } from 'lucide-react'
import Link from 'next/link'

const faqs = [
  {
    q: 'How long does a typical project take?',
    a: 'It depends on scope, but most web and mobile projects run 6–16 weeks. After a short discovery call we give you a clear timeline with milestones so you always know what to expect.',
  },
  {
    q: 'How much will my project cost?',
    a: 'Every project is quoted individually based on features, complexity, and timeline. We offer fixed-price and dedicated-team models, and we share a transparent estimate before any work begins.',
  },
  {
    q: 'Do you provide ongoing support after launch?',
    a: 'Yes. All projects include a post-launch warranty, and we offer flexible maintenance plans with monitoring, updates, and 24/7 support to keep your product healthy as it grows.',
  },
  {
    q: 'Which technologies do you work with?',
    a: 'We specialize in React, Next.js, Node.js, React Native, and cloud platforms like AWS and Azure, plus AI/ML, blockchain, and IoT. We pick the right stack for your goals, not the other way around.',
  },
  {
    q: 'Will I own the code and intellectual property?',
    a: 'Absolutely. Once the project is paid in full, you own 100% of the source code and IP. We also hand over documentation and repositories so your team can take over anytime.',
  },
  {
    q: 'Can you work with my existing team or codebase?',
    a: 'Yes. We regularly augment in-house teams and improve existing systems. We start with a code and architecture review, then integrate smoothly into your workflow and tooling.',
  },
]

export default function FAQ() {
  const [open, setOpen] = useState<number | null>(0)

  return (
    <section className="py-20 sm:py-28 bg-white relative overflow-hidden">
      <div className="absolute top-10 right-0 w-80 h-80 bg-sky-100/30 rounded-full blur-3xl" />
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 text-sky-600 text-sm font-medium mb-4">
            <HelpCircle className="w-4 h-4" /> FAQ
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto">
            Everything you need to know before we get started
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((item, i) => {
            const isOpen = open === i
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`rounded-2xl border transition-colors duration-300 ${
                  isOpen ? 'border-sky-200 bg-sky-50/40 shadow-sm' : 'border-slate-100 bg-white hover:border-sky-100'
                }`}
              >
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left"
                >
                  <span className={`font-semibold ${isOpen ? 'text-sky-700' : 'text-slate-900'}`}>{item.q}</span>
                  <motion.span
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.25 }}
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      isOpen ? 'bg-sky-500 text-white' : 'bg-sky-100 text-sky-600'
                    }`}
                  >
                    <Plus className="w-4 h-4" />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-5 text-slate-600 leading-relaxed">{item.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mt-10"
        >
          <p className="text-slate-500 mb-4">Still have questions?</p>
          <Link href="/contact" className="btn-primary inline-flex items-center gap-2">
            Talk to Our Team
          </Link>
        </motion.div>
      </div>
    </section>
  )
}
